// StatusBar (round 2): one bar of states with their counts that filters the table under it (BL-158
// 11): on the board, the day's states (No one yet, Needs a look, Booked, On the way, On site,
// Paused, Done), then, after a rule, the jobs not on today's board (On hold, Unscheduled, Cancelled).
// The chosen one is in ink with the heavier line under it; a count that needs someone now has StateMark's
// red bar before it, its figure in ink (red is a mark, never small text). One
// is always chosen. Narrow, a group wraps as one, and one wider than the bar wraps its tabs, so none is
// ever past its edge (QA-841). At phone widths each tab is a touch target, 44 tall
// (the touch size of the library's controls). A group can say its scope before its tabs ("Today’s
// visits", "Any day", QA-768), so counts of different scopes never read as one. Truck stock (BL-203): the
// Stock page's places take it too: a count can be words ("2 below min", "new"; one that needs someone in
// ink after StateMark's red bar) and a tab can carry a muted second line (who has the place: "Sam K.").
// Reads only --statusbar-*.
import { useId } from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { StateMark } from '../StateMark/StateMark.tsx';
import './StatusBar.css';

export interface StatusTab {
  id: string;
  label: string;
  /** How many; none for a tab with nothing to count (Every place). */
  count?: number;
  /** The count in words, in its place ("2 below min", "new"). */
  says?: string;
  /** A second line under its name, muted (who has the place: "Sam K.", "The shop"). */
  sub?: string;
  /** A count that needs someone now: StateMark's red bar before it when it isn't 0, the count or its
   *  `says` words in ink (red is never small text on the desk). */
  urgent?: boolean;
}

/** What a tab is called for screen readers: "Sam’s van, Sam K.", "Warehouse, 2 below min, The shop". */
function tabName(t: StatusTab): string {
  return [t.label, t.says ?? (t.count !== undefined ? String(t.count) : null), t.sub]
    .filter((x) => x != null && x !== '')
    .join(', ');
}

export interface StatusBarProps {
  /** For screen readers: "Visits by state". */
  label: string;
  /** The tabs in groups, a rule between groups. */
  groups: StatusTab[][];
  selected: string;
  onChange?: (id: string) => void;
  /**
   * One row that never wraps, each tab's words on one line: the box around it scrolls sideways when
   * it's narrow (History's tabs on a phone, pages/job-history-option). Otherwise it wraps by group.
   */
  oneRow?: boolean;
  /**
   * Each group's scope, said before its tabs and as each of its tabs' description ("Today’s visits",
   * "Any day"); null or absent for a group that needs none. The tabs' names stay "<label>, <count>".
   */
  scopes?: readonly (string | null)[];
}

export function StatusBar({ label, groups, selected, onChange, oneRow, scopes }: StatusBarProps) {
  const id = useId();
  return (
    <ToggleButtonGroup
      className={oneRow ? 'fl-statusbar fl-statusbar--one-row' : 'fl-statusbar'}
      aria-label={label}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[selected]}
      onSelectionChange={(keys) => {
        const [k] = [...keys];
        if (k !== undefined) onChange?.(String(k));
      }}
    >
      {groups.map((g, gi) => (
        <div key={gi} className="fl-statusbar__group" data-later={gi > 0 ? '' : undefined}>
          {scopes?.[gi] ? (
            <span id={`${id}-scope-${gi}`} className="fl-statusbar__scope">
              {scopes[gi]}
            </span>
          ) : null}
          {g.map((t) => (
            <ToggleButton
              key={t.id}
              id={t.id}
              className="fl-statusbar__tab"
              data-sub={t.sub ? '' : undefined}
              aria-label={tabName(t)}
              {...(scopes?.[gi] ? { 'aria-describedby': `${id}-scope-${gi}` } : {})}
            >
              <span className="fl-statusbar__label">{t.label}</span>
              {t.says ? (
                <span className="fl-statusbar__says">
                  {t.urgent ? <StateMark state="problem" /> : null}
                  {t.says}
                </span>
              ) : t.count !== undefined ? (
                <span className="fl-statusbar__count" data-urgent={t.urgent && t.count > 0 ? '' : undefined}>
                  {t.urgent && t.count > 0 ? <StateMark state="problem" /> : null}
                  {t.count}
                </span>
              ) : null}
              {t.sub ? <span className="fl-statusbar__sub">{t.sub}</span> : null}
            </ToggleButton>
          ))}
        </div>
      ))}
    </ToggleButtonGroup>
  );
}
